import React, { useEffect, useRef } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import logoFull from '../../assets/images/metalic-logo.png';
import './HeroSection.css';
import '../common-styles.css';

const HeroSection: React.FC = () => {
  const vantaRef = useRef<HTMLElement>(null);
  const vantaEffectRef = useRef<any>(null);

  useEffect(() => {
    /**
     * Problems previously observed:
     *  - VANTA init error: Cannot read properties of undefined (reading 'DoubleSide')
     *    This happens when Vanta can't see a THREE build on window at init time, or a
     *    mismatched THREE version is used. We now: (1) load scripts only if missing,
     *    (2) wait for both globals, (3) pass THREE explicitly in config.
     */

    function initVanta() {
      const w = window as any;
      if (vantaEffectRef.current) return; // already initialised
      if (!w.VANTA || !w.VANTA.WAVES) return; // script not ready yet
      if (!w.THREE) return; // three not ready yet
      if (!vantaRef.current) return; // element not mounted

      try {
        vantaEffectRef.current = w.VANTA.WAVES({
          el: vantaRef.current,
          THREE: w.THREE, // pass explicitly to avoid undefined inside Vanta
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200.0,
          minWidth: 200.0,
          scale: 1.0,
          scaleMobile: 1.0,
          color: 0x02021f,
          // Optional extra tuning (commented for now):
          // shininess: 30.0,
          // waveHeight: 15.0,
          // waveSpeed: 1.0,
          // zoom: 1.0,
        });
      } catch (e) {
        console.error('[HeroSection] Vanta init failed', e);
      }
    }

    // Load external scripts only if needed.
    function loadScript(src: string) {
      return new Promise<void>((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Failed to load ' + src));
        document.head.appendChild(s);
      });
    }

    let cancelled = false;

    (async () => {
      try {
        // Use a THREE version known to work with current Vanta (r121 is the one Vanta docs reference; r134 also works but can break some effects).
        if (!(window as any).THREE) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r121/three.min.js');
        }
        if (!(window as any).VANTA || !(window as any).VANTA.WAVES) {
          await loadScript('https://cdn.jsdelivr.net/npm/vanta@0.5.24/dist/vanta.waves.min.js');
        }

        // Poll a few times in case onload timing still races.
        let attempts = 0;
        const maxAttempts = 5;
        const interval = 120; // ms
        while (!cancelled && attempts < maxAttempts && (!(window as any).THREE || !(window as any).VANTA || !(window as any).VANTA.WAVES)) {
          attempts++;
          await new Promise(r => setTimeout(r, interval));
        }
        if (!cancelled) initVanta();
      } catch (e) {
        console.error('Failed to load Vanta.js or Three.js', e);
      }
    })();

    return () => {
      cancelled = true;
      if (vantaEffectRef.current) {
        try { vantaEffectRef.current.destroy(); } catch (_) { /* ignore */ }
        vantaEffectRef.current = null;
      }
    };
  }, []);

  return (
    <section className="hero-section" ref={vantaRef}>
      {/* Glow placed outside overlay so it sits behind content */}
      <div className="hero-top-glow" aria-hidden="true" />
      
      <div className="hero-overlay">
        <Container fluid>
          <Row className="justify-content-center align-items-center min-vh-100">
            <Col xs={12} className="text-center">
              <div className="hero-content">
                <div className="logo-container">
                  <img 
                    src={logoFull} 
                    alt="Sundiam Logo" 
                    className="hero-logo"
                  />
                </div>
                <h1 className="hero-title">
                  Radiance That Transcends Borders
                </h1>
                <p className="hero-subtitle">
                  Sun Diam - Where Brilliance Meets Craftsmanship
                </p>
                <p className="hero-description">
                  Unveiling nature's rarest treasures with precision, passion, and global reach.<br />
                  From ethically sourced rough to masterfully cut diamonds, we deliver unmatched quality across continents.
                </p>
                <div className="hero-buttons">
                  <Button className="section-btn">
                    <span className="btn-content">
                      Explore Our Collection
                      <i className="fas fa-arrow-right ms-2 btn-arrow-diagonal"></i>
                    </span>
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </section>
  );
};

export default HeroSection;
